import React, { useState } from "react";
import { useBreakpoint } from "../../../hooks";
import { useCustomTranslation } from "../../../hooks/useCustomTranslation";
import { Icon, IconGlyph } from "../../../lib/Icon";
import Tooltip from "../../../lib/Tooltip";
import { IAssetGallery } from "../../../stores/WebGalleryStore";
import { formatBytes, formatDate, generateTooltipContent, isAssetSharedAsOriginal } from "../../../util";
import { DownloadButton } from "../../partials/DownloadButton";

export interface IDetailMetaProps {
  asset?: IAsset;
  gallery?: IAssetGallery;
  onCopy?: () => void;
}

export const DetailMeta = (props: IDetailMetaProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  let { t } = useCustomTranslation("");
  const { asset, gallery } = props;
  const galleryDateoptions = {
    val: new Date(gallery == null ? '' : gallery.sharedAt),
    formatParams: {
      val: {
      //  weekday: 'short',
        day: 'numeric',
        month: 'long', 
        year: 'numeric',       
      }
    }
  };
  const { isDesktop } = useBreakpoint();

  const handleTooltip = () => {
    setIsVisible(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 2000);
  };

  const handleCopy = (text: string) => {
    const textArea = document.createElement("textarea");
    document.body.appendChild(textArea);
    textArea.value = text;
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    handleTooltip();
  };

  const renderTooltipContent = () => {

    const sharedRenditionFileSizeFormatted = formatBytes(asset?.file.sharedRenditionFileSizeInBytes);

    const tooltipContentParts = generateTooltipContent(asset?.file.sharedRenditionFileExtension, asset?.file.sharedRenditionWidth, asset?.file.sharedRenditionHeight, sharedRenditionFileSizeFormatted);

    return (
      <>
        <div>{t("Header.RenditionAvailable")}</div>
        <div>{tooltipContentParts.join(' • ')}</div>
      </>
    )
  }

  return (
    <section className="detail__meta u-word-wrap">
      <div className="detail__meta-header">
        {asset?.title && (
          <div className="detail__meta-heading-container">
            <header className="detail__meta-heading" title={asset.title}>
              {asset.title}
            </header>
            {isVisible && <div className="detail__meta-tooltip">{t("DetailInfo.Copied")}!</div>}
            <CopySVG onCopy={() => handleCopy(asset?.title)} />
          </div>
        )}
        {!isDesktop && asset?.media.download && (
          <div className="download-button__wrap">
            <DownloadButton variant="basic" href={asset.media.download} label={t("DetailInfo.Download") as string} />
            {/* this is to make sure there's no tooltip for older web galleries which don't have this info */}
            {asset?.file.sharedRenditionFileExtension && !isAssetSharedAsOriginal(asset.media.download) && (
              <Tooltip
                wrapperClassName={"assets-browser__top-bar-tooltip"}
                tooltipContent={renderTooltipContent()}
                position="down"
                trigger="click"
              >
                <Icon glyph={IconGlyph.QUESTION} />
              </Tooltip>
            )}
          </div>

        )}
      </div>
      <div className="detail__meta-section">
        {asset?.file.fileType && (
          <div className="detail__meta-item">
            <h5 className="detail__meta-label">{t("DetailInfo.Format")}</h5>
            <h5 className="detail__meta-info">{asset.file.fileType}</h5>
          </div>
        )}
        {asset && asset.file.imageWidth !== 0 && asset.file.imageHeight !== 0 && (
          <div className="detail__meta-item">
            <h5 className="detail__meta-label">{t("DetailInfo.Dimensions")}</h5>
            <h5 className="detail__meta-info">
              {asset.file.imageWidth} x {asset.file.imageHeight} pixels
            </h5>
          </div>
        )}
        {asset?.file.sizeInBytes && (
          <div className="detail__meta-item">
            <h5 className="detail__meta-label">{t("DetailInfo.FileSize")}</h5>
            <h5 className="detail__meta-info">
              {formatBytes(asset.file.sizeInBytes)}
            </h5>
          </div>
        )}
        {gallery?.sharedBy.name && (
          <div className="detail__meta-item">
            <h5 className="detail__meta-label">{t("DetailInfo.AssetsSharedBy")}</h5>
            <h5 className="detail__meta-info">{gallery.sharedBy.name}</h5>
          </div>
        )}
        {gallery?.sharedBy.organization && (
          <div className="detail__meta-item">
            <h5 className="detail__meta-label">{t("DetailInfo.Company")}</h5>
            <h5 className="detail__meta-info">
              {gallery && gallery.sharedBy.organization}
            </h5>
          </div>
        )}
        {gallery && (
          <div className="detail__meta-item">
            <h5 className="detail__meta-label">{t("DetailInfo.Date")}</h5>
            <h5 className="detail__meta-info">
              {t("{{val, datetime}}", galleryDateoptions)}
            </h5>
          </div>
        )}
      </div>
      {asset?.description && (
        <div className="detail__meta-section detail__meta-description">
          <div className="detail__meta-item">
            <h5 className="detail__meta-label">{t("DetailInfo.Description")}</h5>
            <h5 className="detail__meta-info">{asset.description}</h5>
          </div>
        </div>
      )}
    </section>
  );
};

const CopySVG = ({ onCopy }: IDetailMetaProps) => {
  return (
    <>
      <div className="detail__meta-copy">
        <Icon glyph={IconGlyph.COPY} onClick={onCopy} />
      </div>
    </>
  );
};
